<script setup lang="ts">
import { ButtonHTMLAttributes, computed } from 'vue'

import Icon from '../Icon/Icon.vue'
import { IconName, IconProps } from '../Icon/types'
import { ButtonSize, ButtonWidth, FontSize } from './types'
import { buttonCva, ButtonProps } from './variants'

type Props = /* @vue-ignore */ ButtonHTMLAttributes & {
  size?: ButtonSize
  width?: ButtonWidth
  icon?: IconName | ''
  iconSize?: IconProps['size']
  disabled?: boolean
  selected?: boolean
  title?: string
  square?: boolean
  fontSize?: FontSize
  variant?: ButtonProps['variant']
  visible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: '1x',
  visible: true,
})

// methods
function onClick(e: MouseEvent): void {
  if (props.disabled) {
    e.preventDefault()
    e.stopPropagation()
  } else {
    ;(e.currentTarget as HTMLButtonElement).blur()
  }
}

// computed
const shape = computed(() =>
  props.square ? 'square' : props.size !== undefined ? 'rectangle' : undefined,
)

const fontSize = computed<FontSize | ''>(() => {
  if (props.fontSize) return props.fontSize
  if (props.size === 'xs' && shape.value === 'rectangle' && !props.width)
    return 'text-xs'
  if (shape.value) return 'text-sm'

  return ''
})
</script>

<template>
  <button
    class="border text-nowrap select-none"
    :class="[
      {
        // cursor
        'cursor-default':
          $attrs.type !== 'submit' && $attrs.onClick === undefined,

        // clickable button
        'cursor-pointer hover:transition hover:duration-150 hover:ease-in focus:outline-hidden focus:duration-75':
          ($attrs.type === 'submit' || $attrs.onClick !== undefined) &&
          !disabled,

        // font size
        [fontSize]: fontSize,

        // based styled button
        'inline-flex items-center justify-center rounded-sm': shape,
        'text-neutral-dark-10/50': shape && disabled,
        'border-neutral-light-3': shape && disabled && !variant,

        // rectangle
        'w-full': width === 'full' && shape === 'rectangle',

        'h-6': size === 'xs' && shape === 'rectangle',
        'px-0.5': size === 'xs' && shape === 'rectangle' && width === 'slim',
        'px-2': size === 'xs' && shape === 'rectangle' && !width,
        'gap-0.5': size === 'xs' && shape === 'rectangle' && $slots.default,

        'h-8': size === 'sm' && shape === 'rectangle',
        'px-3': size === 'sm' && shape === 'rectangle' && !width,
        'px-1': size === 'sm' && shape === 'rectangle' && width === 'slim',
        'gap-1': size === 'sm' && shape === 'rectangle' && $slots.default,

        'h-10': size === 'md' && shape === 'rectangle',
        'px-4.5': size === 'md' && shape === 'rectangle' && !width,
        'gap-2': size === 'md' && shape === 'rectangle' && $slots.default,

        'h-12': size === 'lg' && shape === 'rectangle',
        'px-5': size === 'lg' && shape === 'rectangle' && !width,
        'gap-2.5': size === 'lg' && shape === 'rectangle' && $slots.default,

        // square
        'max-h-4 min-h-4 max-w-4 min-w-4': size === '2xs' && shape === 'square',
        'max-h-6 min-h-6 max-w-6 min-w-6': size === 'xs' && shape === 'square',
        'max-h-8 min-h-8 max-w-8 min-w-8': size === 'sm' && shape === 'square',
        'max-h-12 min-h-12 max-w-12 min-w-12':
          size === 'lg' && shape === 'square',

        invisible: !visible,
      },
      buttonCva({ variant, disabled, selected }),
    ]"
    type="button"
    :disabled
    :title
    @click="onClick"
  >
    <Icon
      v-if="icon !== undefined"
      :class="{
        'px-0.75 pb-0.25': size === 'sm' && shape === 'rectangle',
        invisible: icon === '',
      }"
      :icon="icon === '' ? 'square' : icon"
      :size="iconSize"
    />
    <slot />
  </button>
</template>
